import { ArchivedView, ListContentFilter, ListOwnership } from "@/enum/list.enum";

/**
 * Readers for the Lead List overview query params. Each derives a single piece of overview state
 * from the current URL so the {@link ListTable} state can be initialised from a deep link.
 */
const params = () => new URLSearchParams(window.location.search);

export const getSearchTermFromUrl = (): string => params().get("list") || "";

export const getContentFilterFromUrl = (): ListContentFilter =>
	(params().get("filter") as ListContentFilter) || ListContentFilter.ALL;

export const getArchivedViewFromUrl = (): ArchivedView =>
	params().get("archived") === ArchivedView.SHOW ? ArchivedView.SHOW : ArchivedView.HIDE;

export const getListTypeFromUrl = (): string | undefined => params().get("type") || undefined;

export const getOwnershipFromUrl = (): ListOwnership =>
	(params().get("ownership") as ListOwnership) || ListOwnership.MINE;

export const getSearchOwnershipFromUrl = (): ListOwnership =>
	(params().get("searchOwnership") as ListOwnership) || ListOwnership.ALL;

export const isSearchActiveFromUrl = (): boolean => !!params().get("list")?.trim();

/** The List overview state that is mirrored into the URL query string. */
export interface ListOverviewParamsState {
	contentFilter: ListContentFilter;
	archivedView: ArchivedView;
	selectedSourceType?: string;
	ownership: ListOwnership;
	searchOwnership: ListOwnership;
}

/**
 * Build the List overview query string from the current overview state, preserving any unrelated
 * params already on the URL. Each value is written only when it differs from its default so the URL
 * stays clean on the default view.
 *
 * The *omitted* default for each param must match the matching `get*FromUrl` reader above, or a
 * reloaded clean URL is read back as a different view. PREC-3622: the `ownership` writer used to
 * treat ALL as the default while {@link getOwnershipFromUrl} defaults to MINE, so the All-lists tab
 * — which produced a clean URL — reloaded as My-lists. MINE is the default tab, so persist TEAM and
 * ALL and only omit MINE.
 */
export const buildListOverviewSearch = (
	currentSearch: string,
	{
		contentFilter,
		archivedView,
		selectedSourceType,
		ownership,
		searchOwnership,
	}: ListOverviewParamsState
): string => {
	const params = new URLSearchParams(currentSearch);

	if (contentFilter && contentFilter !== ListContentFilter.ALL) {
		params.set("filter", contentFilter); // ALL is the default → keep URL clean
	} else {
		params.delete("filter");
	}

	if (archivedView === ArchivedView.SHOW) {
		params.set("archived", ArchivedView.SHOW); // HIDE is the default → keep URL clean
	} else {
		params.delete("archived");
	}

	if (selectedSourceType) {
		params.set("type", selectedSourceType); // Source-type filter
	} else {
		params.delete("type");
	}

	if (ownership && ownership !== ListOwnership.MINE) {
		params.set("ownership", ownership); // MINE is the default tab → keep URL clean (PREC-3622)
	} else {
		params.delete("ownership");
	}

	if (searchOwnership && searchOwnership !== ListOwnership.ALL) {
		params.set("searchOwnership", searchOwnership); // ALL is the default → keep URL clean
	} else {
		params.delete("searchOwnership");
	}

	return params.toString();
};
